import { Mail } from 'lucide-react';

export default function Accessibility() {
  const lastUpdated = '2026-05-07';
  const contactEmail = 'service@northbridgehvac.ca';

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-[var(--color-slate-blue)] text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Accessibility Statement
          </h1>
          <p className="text-xl text-gray-100 max-w-3xl">
            Our commitment to making this website usable by every homeowner in
            York Region.
          </p>
          <p className="mt-4 text-sm text-gray-200">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">
          <article className="space-y-4 text-gray-700 leading-relaxed">
            <h2 className="text-2xl font-bold text-[var(--color-text-dark)]">
              1. Our Commitment
            </h2>
            <p>
              Northbridge HVAC Inc. is committed to making{' '}
              <strong>northbridgehvac.ca</strong> usable by the widest possible
              audience, including people with visual, auditory, motor, and
              cognitive disabilities. We aim to meet the requirements of the{' '}
              <em>Accessibility for Ontarians with Disabilities Act, 2005</em>{' '}
              (AODA) and to conform to the{' '}
              <strong>
                Web Content Accessibility Guidelines (WCAG) 2.0 Level AA
              </strong>
              , the standard adopted by Ontario&rsquo;s Integrated
              Accessibility Standards Regulation (O.&nbsp;Reg.&nbsp;191/11)
              under AODA.
            </p>
          </article>

          <article className="space-y-4 text-gray-700 leading-relaxed">
            <h2 className="text-2xl font-bold text-[var(--color-text-dark)]">
              2. Standards We Follow
            </h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>WCAG 2.0 Level AA</strong> &mdash; the international
                standard for accessible web content, published by the World
                Wide Web Consortium (W3C).
              </li>
              <li>
                <strong>AODA</strong> &mdash; the <em>Accessibility for
                Ontarians with Disabilities Act, 2005</em>.
              </li>
              <li>
                <strong>O.&nbsp;Reg.&nbsp;191/11 (IASR)</strong> &mdash; the
                Integrated Accessibility Standards Regulation, Part&nbsp;II
                (Information and Communications Standards), which incorporates
                WCAG 2.0 Level AA for public-facing websites.
              </li>
            </ul>
          </article>

          <article className="space-y-4 text-gray-700 leading-relaxed">
            <h2 className="text-2xl font-bold text-[var(--color-text-dark)]">
              3. How We Achieve It
            </h2>
            <p>This site is designed and built with the following measures:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Semantic HTML</strong> &mdash; meaningful headings,
                lists, landmarks, and form controls so the page structure is
                conveyed to assistive technologies. Heading levels follow a
                logical order (H1 &rarr; H2 &rarr; H3) without skipping.
              </li>
              <li>
                <strong>Keyboard navigation</strong> &mdash; every interactive
                element is reachable and operable using a keyboard alone. A
                &ldquo;Skip to main content&rdquo; link is provided at the top
                of each page.
              </li>
              <li>
                <strong>Alt text</strong> &mdash; informative images carry
                descriptive alternative text; decorative images are marked so
                that screen readers skip them.
              </li>
              <li>
                <strong>Colour contrast</strong> &mdash; text and interactive
                elements meet or exceed the AA contrast ratios (4.5:1 for
                normal text, 3:1 for large text).
              </li>
              <li>
                <strong>Visible focus indicators</strong> &mdash; every
                focusable element shows a visible outline or ring when reached
                by keyboard.
              </li>
              <li>
                <strong>Forms and labels</strong> &mdash; every form input has
                a programmatically associated label, and required fields and
                error messages are announced to assistive technologies.
              </li>
              <li>
                <strong>ARIA</strong> &mdash; ARIA attributes and landmark
                roles are used where native HTML semantics fall short.
              </li>
              <li>
                <strong>Responsive layout</strong> &mdash; content reflows
                cleanly from 320px screen widths up to large desktop displays.
              </li>
              <li>
                <strong>Automated checks</strong> &mdash; every build runs
                axe-core / Lighthouse accessibility checks. Builds that fail
                those checks do not deploy.
              </li>
            </ul>
          </article>

          <article className="space-y-4 text-gray-700 leading-relaxed">
            <h2 className="text-2xl font-bold text-[var(--color-text-dark)]">
              4. Known Limitations
            </h2>
            <p>
              We test in modern browsers (Chrome, Firefox, Safari, Edge) with
              current versions of NVDA, JAWS, and VoiceOver. Some third-party
              embeds (for example, scheduling widgets or map embeds) may not
              be fully under our control; where we identify a gap we work
              with the vendor or replace the component.
            </p>
          </article>

          <article className="space-y-4 text-gray-700 leading-relaxed">
            <h2 className="text-2xl font-bold text-[var(--color-text-dark)]">
              5. Feedback &amp; Complaints
            </h2>
            <p>
              If you encounter a barrier on this site, or if you have a
              suggestion for improving accessibility, please tell us. We aim
              to respond within <strong>5 business days</strong>.
            </p>
            <p className="flex items-center gap-2">
              <Mail size={18} className="text-[var(--color-slate-blue)]" />
              <a
                href={`mailto:${contactEmail}`}
                className="text-[var(--color-slate-blue)] underline font-semibold"
              >
                {contactEmail}
              </a>
            </p>
            <p>Please include:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>The page or feature where you encountered the barrier;</li>
              <li>The browser and assistive technology you were using (if any);</li>
              <li>A description of the issue or your suggestion.</li>
            </ul>
            <p>
              If you are not satisfied with our response, you may also escalate
              your concern to the Accessibility Directorate of Ontario, which
              administers AODA on behalf of the Government of Ontario.
            </p>
          </article>

          <article className="space-y-4 text-gray-700 leading-relaxed">
            <h2 className="text-2xl font-bold text-[var(--color-text-dark)]">
              6. Alternative Formats
            </h2>
            <p>
              On request, we will work with you to provide the information on
              this site in a format that meets your needs. Email{' '}
              <a
                href={`mailto:${contactEmail}`}
                className="text-[var(--color-slate-blue)] underline"
              >
                {contactEmail}
              </a>{' '}
              and we will confirm a reasonable timeline based on the format
              requested.
            </p>
          </article>

          <article className="space-y-4 text-gray-700 leading-relaxed">
            <h2 className="text-2xl font-bold text-[var(--color-text-dark)]">
              7. Changes to This Statement
            </h2>
            <p>
              We update this statement when our practices, technology, or the
              underlying regulations change. The &ldquo;Last updated&rdquo;
              date at the top of this page reflects the most recent revision.
            </p>
          </article>

          <div className="bg-[var(--color-neutral-bg)] rounded-lg p-4 mt-12">
            <p className="text-sm text-gray-600 italic">
              <strong>Demo note:</strong> Northbridge HVAC is a fictional
              business shown as a portfolio demonstration. The contact email
              above is illustrative and is not monitored.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
